import { memo, useCallback, useMemo, type ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDragScroll } from "@/hooks/useDragScroll";
import { useHorizontalWheelScrollRef } from "@/hooks/useHorizontalWheelScroll";
import { usePreloadStashes } from "@/hooks/usePreloadStashes";
import { STASH_DETAIL } from "@/routes";
import {
  StashThumbnail,
  type StashThumbnailData,
  type ThumbnailWidth,
  type ThumbnailRadius,
} from "./StashThumbnail";
import { Link } from "react-router-dom";
import { CONTROL } from "@/lib/typography";

export interface StashThumbnailListProps {
  posts: StashThumbnailData[];
  onViewAllClick?: () => void;
  viewAllHref?: string;
  className?: string;

  // Display options
  showViewAll?: boolean;
  maxItems?: number;
  thumbnailClassName?: string;

  // Thumbnail styling (passed to each StashThumbnail)
  itemWidth?: ThumbnailWidth;
  cornerRadius?: ThumbnailRadius;
  aspectRatio?: string;
  hoverScale?: boolean;
  showTitle?: boolean;
  titleClassName?: string;
  loading?: "lazy" | "eager";

  // When true, removes bottom padding (parent controls spacing via gap)
  disablePadding?: boolean;

  // Interactive mode (map stash selection)
  onItemClick?: (post: StashThumbnailData) => void;
  onItemHover?: (post: StashThumbnailData | null) => void;
  renderOverlay?: (post: StashThumbnailData) => ReactNode;
  /** Per-item overlay callback (matches Grid/Masonry API). Falls back to renderOverlay. */
  unavailableIds?: ReadonlySet<string>;
  onRetryItem?: (post: StashThumbnailData) => unknown;
  getItemFooter?: (post: StashThumbnailData) => ReactNode;
  getItemOverlay?: (post: StashThumbnailData) => ReactNode;
  selectedId?: string;

  // Edit/select mode (matches Grid/Masonry API)
  isEditMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (postId: string) => void;

  // Platform icon pass-through
  showPlatformIcon?: boolean;
  platformIconPosition?: "top-left" | "top-right";

  // Collection context for back-to-collection navigation
  collectionContext?: { collectionId: string; inviteCode?: string };
  enableVerticalWheelScroll?: boolean;
}

const buildScrollContainerClassName = (className?: string, disablePadding = false) =>
  cn(
    "flex gap-2.5 items-start overflow-x-auto scrollbar-hide",
    !disablePadding && "pb-2",
    "-mx-gutter px-gutter",
    className,
  );


export const StashThumbnailList = memo(
  ({
    posts,
    onViewAllClick,
    viewAllHref,
    className,
    showViewAll = false,
    maxItems = 15,
    thumbnailClassName,
    itemWidth = "fixed",
    cornerRadius = "2xl",
    aspectRatio = "aspect-[9/16]",
    hoverScale = true,
    showTitle,
    titleClassName,
    loading = "lazy",
    disablePadding = false,
    onItemClick,
    onItemHover,
    renderOverlay,
    getItemOverlay,
    getItemFooter,
    unavailableIds,
    onRetryItem,
    selectedId,
    isEditMode,
    selectedIds,
    onToggleSelect,
    showPlatformIcon,
    platformIconPosition,
    collectionContext,
    enableVerticalWheelScroll = false,
  }: StashThumbnailListProps) => {
    usePreloadStashes(posts);
    const displayPosts = useMemo(() => posts.slice(0, maxItems), [posts, maxItems]);
    const dragScrollRef = useDragScroll<HTMLDivElement>();
    const horizontalWheelRef = useHorizontalWheelScrollRef<HTMLDivElement>(enableVerticalWheelScroll);
    const setScrollContainerRef = useCallback((node: HTMLDivElement | null) => {
      horizontalWheelRef.current = node;
      dragScrollRef(node);
    }, [dragScrollRef, horizontalWheelRef]);
    const showViewAllCard = Boolean(showViewAll && displayPosts.length >= maxItems && (onViewAllClick || viewAllHref));


    const scrollContainerClassName = buildScrollContainerClassName(className, disablePadding);

    return (
      <div ref={setScrollContainerRef} className={scrollContainerClassName}>
        {displayPosts.map(post => (
          <StashThumbnail
            key={post.id}
            post={post}
            href={
              onItemClick || onToggleSelect
                ? undefined
                : collectionContext
                  ? `${STASH_DETAIL({ stashId: post.id })}?collection=${collectionContext.collectionId}${collectionContext.inviteCode ? `&invite=${collectionContext.inviteCode}` : ""}`
                  : STASH_DETAIL({ stashId: post.id })
            }
            onClick={onItemClick ? () => onItemClick(post) : undefined}
            onMouseEnter={onItemHover ? () => onItemHover(post) : undefined}
            onMouseLeave={onItemHover ? () => onItemHover(null) : undefined}
            onFocus={onItemHover ? () => onItemHover(post) : undefined}
            onBlur={onItemHover ? () => onItemHover(null) : undefined}
            width={itemWidth}
            cornerRadius={cornerRadius}
            aspectRatio={aspectRatio}
            hoverScale={hoverScale}
            showTitle={showTitle}
            titleClassName={titleClassName}
            loading={loading}
            className={cn(thumbnailClassName, selectedId && selectedId !== post.id && "opacity-40 transition-opacity")}
            overlay={getItemOverlay?.(post) ?? renderOverlay?.(post)}
                footer={getItemFooter?.(post)}
                unavailable={unavailableIds?.has(post.id)}
                onRetry={onRetryItem ? () => onRetryItem(post) : undefined}
            isEditMode={isEditMode}
            isSelected={selectedIds?.has(post.id)}
            onToggleSelect={onToggleSelect}
            showPlatformIcon={showPlatformIcon}
            platformIconPosition={platformIconPosition}
          />
        ))}

        {/* View All card */}
        {showViewAllCard &&
          (viewAllHref ? (
            <Link to={viewAllHref} className="w-[192px] flex-shrink-0">
              <div
                className={cn(
                  "relative w-full overflow-hidden border border-dashed bg-card flex items-center justify-center",
                  aspectRatio,
                  "rounded-2xl",
                )}
              >
                <div className="flex flex-col items-center gap-2">
                  <span className={CONTROL}>View all</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </Link>
          ) : (
            <div onClick={onViewAllClick} className="w-[192px] flex-shrink-0">
              <div
                className={cn(
                  "relative w-full overflow-hidden border border-dashed bg-card flex items-center justify-center",
                  aspectRatio,
                  "rounded-2xl",
                )}
              >
                <div className="flex flex-col items-center gap-2">
                  <span className={CONTROL}>View all</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          ))}
      </div>
    );
  },
);

StashThumbnailList.displayName = "StashThumbnailList";
